import { FREE_TRIAL_DAYS } from 'constants/billing';
import { BannerPosition } from 'features/banner/model/banner';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellCardBaseProps, UpsellCardSpotlightProps } from '../../upsell-card/models/upsell-card-props';
import { UsageStatus } from './useMonthlyOrderUsage';
import { useShowFreeTrial } from './useShowFreeTrial';
import { useUpgradePlan } from 'hooks/billing/useUpgradePlan';

/**
 * Spotlight upsell card props for the given monthly order usage status.
 */
export const useUsageUpsellCard = (status: UsageStatus): UpsellCardBaseProps & UpsellCardSpotlightProps => {
    const { t } = useTranslation();
    const { goToPlan } = useCurrentSubscriptionHelper();
    const showFreeTrial = useShowFreeTrial();
    const { name: upgradePlanName, handle: upgradePlanHandle } = useUpgradePlan();

    return useMemo((): UpsellCardBaseProps & UpsellCardSpotlightProps => {
        // Straight to the upgrade plan's own page (monthly billing)
        const action = { onClick: () => goToPlan(upgradePlanHandle) };
        const subscribeLabel = t('DASHBOARD.UPSELL_CARD.UPGRADE_PLAN', { plan: upgradePlanName });

        switch (status) {
            case 'LIMIT_REACHED':
                return {
                    variant: 'spotlight',
                    theme: 'lavender',
                    badge: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.BADGE'),
                    title: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.DESCRIPTION'),
                    action: {
                        ...action,
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.ACTION', {
                                  count: FREE_TRIAL_DAYS,
                                  plan: upgradePlanName,
                              })
                            : subscribeLabel,
                    },
                    position: BannerPosition.DASHBOARD_PAGE,
                };
            case 'ALMOST_FULL':
                return {
                    variant: 'spotlight',
                    theme: 'mint',
                    eyebrow: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.EYEBROW'),
                    title: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.DESCRIPTION'),
                    action: {
                        ...action,
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.ACTION', { count: FREE_TRIAL_DAYS })
                            : subscribeLabel,
                    },
                };
            case 'RUNNING_LOW':
                return {
                    variant: 'spotlight',
                    theme: 'dark',
                    eyebrow: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.EYEBROW'),
                    title: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.DESCRIPTION'),
                    action: {
                        ...action,
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.ACTION', { count: FREE_TRIAL_DAYS })
                            : subscribeLabel,
                    },
                };
            default:
                return {
                    variant: 'spotlight',
                    theme: 'lime',
                    badge: t('DASHBOARD.UPSELL_CARD.ON_TRACK.BADGE', { plan: upgradePlanName }),
                    title: t('DASHBOARD.UPSELL_CARD.ON_TRACK.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.ON_TRACK.DESCRIPTION'),
                    action: {
                        ...action,
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.ON_TRACK.ACTION', { count: FREE_TRIAL_DAYS })
                            : subscribeLabel,
                    },
                };
        }
    }, [status, showFreeTrial, upgradePlanName, upgradePlanHandle, goToPlan, t]);
};
